'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { ChevronLeft, ChevronRight } from '@/components/ui/icons'
import { SocialIcon } from '@/components/ui/social-icons'
import { MAX_CLUB_SOCIALS, type ClubSocial } from '@/lib/socials'
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion'

export interface ClubCarouselItem {
  slug: string
  name: string
  shortName: string
  logoUrl: string | null
  /** Tutte le società accreditate hanno una pagina pubblica `/<slug>`. */
  tier: 'base' | 'esteso' | 'completo'
  /** Social del club (max 4): mostrati come iconcine in linea sul badge. */
  socials: ClubSocial[]
}

/**
 * Club accreditati su due file che scorrono in orizzontale.
 *
 * - `lg` e oltre: pagine da 8 = **2 file da 4** (colonne 4, righe 2).
 * - sotto `lg`: pagine da 4 = 2 file da 2, stessa logica senza cambiare markup.
 *
 * L'altezza del contenitore resta quella di due file (`--club-tile` × 2 + gap)
 * anche quando una pagina è parziale, così il ricambio di `perPage` al mount non
 * sposta la sezione.
 */
const LG_QUERY = '(min-width: 1024px)'
const PER_PAGE_LG = 8
const PER_PAGE_SM = 4

/** Altezza di una tile + `gap-3` (0.75rem): due file di tile. */
const TILE_H = 148
const ROW_GAP = 12

function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = []
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size))
  return out
}

function ClubTile({ club }: { club: ClubCarouselItem }) {
  // Ogni club accreditato ha una pagina pubblica (`/<slug>`), anche col piano
  // base: la tile è sempre un link.
  const socials = (club.socials ?? []).slice(0, MAX_CLUB_SOCIALS)
  const tileClass = 'league-card league-club-tile relative block min-h-[148px] overflow-hidden rounded-2xl'
  const tileStyle = { ['--wm' as string]: club.logoUrl ? `url(/api/logo?src=${encodeURIComponent(club.logoUrl)})` : 'none' }

  return (
    <div
      title={club.name}
      className={`${tileClass} league-press transition-shadow hover:shadow-[var(--league-shadow-md)]`}
      style={tileStyle}
    >
      {!club.logoUrl && (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-3 z-0 flex items-center justify-center font-display text-[36px] font-bold uppercase text-[color:var(--league-muted)] opacity-20"
        >
          {(club.shortName || club.name).slice(0, 3)}
        </span>
      )}
      {/* L'overlay non cattura i clic (il link del club resta cliccabile sotto);
          solo le icone social lo riattivano, così nessun anchor è annidato. */}
      <span className="pointer-events-none absolute inset-x-0 bottom-0 z-20 bg-gradient-to-t from-white via-white/85 to-transparent px-2.5 pb-2.5 pt-7 text-center">
        <span className="line-clamp-2 block text-[12px] font-semibold leading-tight text-[color:var(--league-ink)]">
          {club.shortName || club.name}
        </span>
        {socials.length > 0 && (
          <span className="mt-1 flex items-center justify-center gap-1.5">
            {socials.map((s) => (
              <a
                key={s.network}
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${s.label} di ${club.name}`}
                title={s.label}
                className="pointer-events-auto inline-flex h-6 w-6 items-center justify-center rounded-full text-[color:var(--league-muted)] transition-colors hover:text-[color:var(--league-brand-vivid)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--league-brand-vivid)]"
              >
                <SocialIcon network={s.network} className="h-4 w-4" />
              </a>
            ))}
          </span>
        )}
      </span>
      <Link
        href={`/${club.slug}`}
        aria-label={`Apri la pagina di ${club.name}`}
        className="absolute inset-0 z-10 rounded-2xl"
      />
    </div>
  )
}

export function ClubCarousel({ clubs }: { clubs: ClubCarouselItem[] }) {
  const reduceMotion = usePrefersReducedMotion()
  const scrollerRef = useRef<HTMLDivElement>(null)
  // Mobile-first: al primo render le pagine sono da 4, così sotto `lg` non
  // vengono mai disegnate quattro file in attesa dell'hydration.
  const [perPage, setPerPage] = useState(PER_PAGE_SM)
  const [page, setPage] = useState(0)

  useEffect(() => {
    const query = window.matchMedia(LG_QUERY)
    const update = () => setPerPage(query.matches ? PER_PAGE_LG : PER_PAGE_SM)
    update()
    query.addEventListener('change', update)
    return () => query.removeEventListener('change', update)
  }, [])

  const pages = chunk(clubs, perPage)

  // Pagina attiva = posizione di scroll / larghezza visibile (misurata, non assunta).
  useEffect(() => {
    const el = scrollerRef.current
    if (!el) return
    el.scrollLeft = 0
    setPage(0)
    const onScroll = () => {
      const width = el.clientWidth || 1
      setPage(Math.max(0, Math.round(el.scrollLeft / width)))
    }
    onScroll()
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
  }, [pages.length])

  const goToPage = useCallback(
    (index: number) => {
      const el = scrollerRef.current
      if (!el) return
      const clamped = Math.max(0, Math.min(pages.length - 1, index))
      el.scrollTo({ left: clamped * el.clientWidth, behavior: reduceMotion ? 'auto' : 'smooth' })
    },
    [pages.length, reduceMotion]
  )

  if (clubs.length === 0) {
    return (
      <p className="league-card mt-5 rounded-2xl p-5 text-[14px] text-[color:var(--league-muted)]">
        Nessun club accreditato per la stagione.
      </p>
    )
  }

  return (
    <div className="mt-5">
      <div
        ref={scrollerRef}
        role="region"
        aria-label="Club accreditati"
        tabIndex={0}
        className="flex snap-x snap-mandatory overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {pages.map((items, i) => (
          <div
            key={i}
            className="grid w-full shrink-0 snap-start auto-rows-[148px] grid-cols-2 gap-3 pr-3 last:pr-0 lg:grid-cols-4"
            style={{ minHeight: TILE_H * 2 + ROW_GAP }}
          >
            {items.map((club) => (
              <ClubTile key={club.slug} club={club} />
            ))}
          </div>
        ))}
      </div>

      {pages.length > 1 && (
        <div className="mt-3 flex flex-wrap items-center justify-center gap-1">
          <button
            type="button"
            onClick={() => goToPage(page - 1)}
            disabled={page === 0}
            aria-label="Pagina precedente"
            className="league-press flex min-h-11 min-w-11 items-center justify-center rounded-full border border-[color:var(--league-line)] text-[color:var(--league-ink)] transition-colors hover:border-[color:var(--league-brand-vivid)] disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>

          {pages.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => goToPage(i)}
              aria-label={`Pagina ${i + 1} di ${pages.length}`}
              aria-current={i === page}
              className="league-press flex min-h-11 min-w-11 items-center justify-center rounded-full transition-colors hover:bg-[color:var(--league-surface-alt)]"
            >
              <span
                aria-hidden
                className={`rounded-full ${i === page
                  ? 'h-2 w-6 bg-[color:var(--league-brand-vivid)]'
                  : 'h-2 w-2 bg-[color:var(--league-line-strong)]'
                }`}
              />
            </button>
          ))}

          <button
            type="button"
            onClick={() => goToPage(page + 1)}
            disabled={page >= pages.length - 1}
            aria-label="Pagina successiva"
            className="league-press flex min-h-11 min-w-11 items-center justify-center rounded-full border border-[color:var(--league-line)] text-[color:var(--league-ink)] transition-colors hover:border-[color:var(--league-brand-vivid)] disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  )
}
